import React, { Dispatch, FC, SetStateAction } from 'react';
import { Fab } from 'react-tiny-fab';
import 'react-tiny-fab/dist/styles.css';
import EditActionsIcon from 'assets/img/icons/pencil.svg';
import { isMobile } from 'react-device-detect';
import { useSnackbar } from 'notistack';
import UploadVideoButton from './parts/UploadVideoButton';
import {
    updatePresentationVideo,
    updatePresentationVideoLandscape,
    updateThanksVideo,
    updateThanksVideoLandscape,
} from '../../../services/EventService';

interface Props {
    isCreator: boolean;
    hasAdvertisingEnabled: boolean;
    uuid: string;
    setLoading: Dispatch<SetStateAction<boolean>>;
}

enum VideoType {
    PRESENTATION_VIDEO = 'PRESENTATION_VIDEO',
    PRESENTATION_VIDEO_DESKTOP = 'PRESENTATION_VIDEO_DESKTOP',
    THANKS_VIDEO = 'THANKS_VIDEO',
    THANKS_VIDEO_DESKTOP = 'THANKS_VIDEO_DESKTOP',
}

const EventSpecialActions: FC<Props> = ({ isCreator, hasAdvertisingEnabled, uuid, setLoading }) => {
    const { enqueueSnackbar } = useSnackbar();

    const submitVideo = async (videoFile: File, route?: string) => {
        try {
            const values = new FormData();
            values.append('event_uuid', uuid);

            let response = null;
            switch (route) {
                case VideoType.PRESENTATION_VIDEO:
                    values.append('event_presentation_video', videoFile, videoFile.name);
                    setLoading(true);
                    response = await updatePresentationVideo(values);
                    break;
                case VideoType.PRESENTATION_VIDEO_DESKTOP:
                    values.append('event_presentation_video', videoFile);
                    setLoading(true);
                    response = await updatePresentationVideoLandscape(values);
                    break;
                case VideoType.THANKS_VIDEO:
                    values.append('event_thanks_video', videoFile);
                    setLoading(true);
                    response = await updateThanksVideo(values);
                    break;
                case VideoType.THANKS_VIDEO_DESKTOP:
                    values.append('event_thanks_video', videoFile);
                    setLoading(true);
                    response = await updateThanksVideoLandscape(values);
                    break;
                default:
                    break;
            }

            setLoading(false);

            // @ts-ignore
            const { data } = response;

            if (data.success === false) {
                enqueueSnackbar('Sorry, something went wrong.', { variant: 'error' });
                return;
            }
            enqueueSnackbar('Video uploaded successfully');
        } catch (err: any) {
            setLoading(false);

            if (err && err.response) {
                const { status, data } = err.response;
                if (status === 404) {
                    enqueueSnackbar(data.error.message);
                } else if (status === 400) {
                    enqueueSnackbar(data.error[0].message, { variant: 'error' });
                }
            } else {
                enqueueSnackbar('Sorry, something went wrong.', { variant: 'error' });
            }
        }
    };

    const validateVideo = (videoFile: File, route?: string) => {
        if (!videoFile || !videoFile.type) {
            enqueueSnackbar('Sorry, something went wrong.', { variant: 'error' });
            return;
        }

        let compatibleMimeType = false;

        if (videoFile.type === 'video/mp4' || videoFile.type === 'video/quicktime') {
            compatibleMimeType = true;
        }

        if (!compatibleMimeType) {
            enqueueSnackbar('Only supported .mp4 or .mov', { variant: 'warning' });
            return;
        }

        const MAX_FILE_SIZE = 100 * 1024 * 1024; // X * MEGABYTES * KILOBYTES
        const MAX_FILE_DURATION = 20; // SECONDS

        if (videoFile.size > MAX_FILE_SIZE) {
            enqueueSnackbar('File size exceeded', { variant: 'warning' });
            return;
        }

        try {
            const video = document.createElement('video');
            video.preload = 'metadata';

            video.onloadedmetadata = function () {
                window.URL.revokeObjectURL(video.src);
                const { duration } = video;

                if (duration > MAX_FILE_DURATION) {
                    enqueueSnackbar(`Video can not exceed ${MAX_FILE_DURATION} duration.`, {
                        variant: 'warning',
                    });
                    return;
                }

                submitVideo(videoFile, route);
            };

            video.src = URL.createObjectURL(videoFile);
        } catch (error) {
            enqueueSnackbar('Sorry, something went wrong.', { variant: 'error' });
        }
    };

    const handlePresentationVideoUpload = (videoFile: File) => {
        validateVideo(videoFile, VideoType.PRESENTATION_VIDEO);
    };
    const handlePresentationVideoDesktopUpload = (videoFile: File) => {
        validateVideo(videoFile, VideoType.PRESENTATION_VIDEO_DESKTOP);
    };
    const handleThanksVideoUpload = (videoFile: File) => {
        validateVideo(videoFile, VideoType.THANKS_VIDEO);
    };
    const handleThanksVideoDesktopUpload = (videoFile: File) => {
        validateVideo(videoFile, VideoType.THANKS_VIDEO_DESKTOP);
    };

    if (!isCreator || !hasAdvertisingEnabled || isMobile) {
        return null;
    }

    return (
        <Fab
            alwaysShowTitle
            mainButtonStyles={{
                backgroundColor: '#ff5a60',
            }}
            icon={
                <img
                    style={{
                        width: 30,
                    }}
                    src={EditActionsIcon}
                    alt=""
                />
            }
        >
            <UploadVideoButton
                text="Upload promotional desktop video"
                handleSubmit={handlePresentationVideoDesktopUpload}
            />
            <UploadVideoButton
                text="Upload promotional mobile video"
                handleSubmit={handlePresentationVideoUpload}
            />
            <UploadVideoButton
                text="Upload post purchase promotional desktop video"
                handleSubmit={handleThanksVideoDesktopUpload}
            />
            <UploadVideoButton
                text="Upload post purchase promotional mobile video"
                handleSubmit={handleThanksVideoUpload}
            />
        </Fab>
    );
};

export default EventSpecialActions;
